import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, Clock, Handshake, Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import StatCard from '@/components/StatCard'
import { getAllPartnerSlots, listPartners } from '@/api/partners'

function formatShekels(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`
}

function PartnerStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    active: { bg: '#14532d40', text: '#4ade80' },
    trial: { bg: '#78350f40', text: '#fbbf24' },
    suspended: { bg: '#7f1d1d40', text: '#f87171' },
    cancelled: { bg: '#37415160', text: '#9ca3af' },
  }
  const s = styles[status] ?? styles.cancelled
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium capitalize"
      style={{ background: s.bg, color: s.text }}
    >
      {status}
    </span>
  )
}

export default function PartnersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data: partners = [], isLoading: loadingPartners } = useQuery({
    queryKey: ['wa-partners'],
    queryFn: listPartners,
  })

  const { data: allSlots = [], isLoading: loadingSlots } = useQuery({
    queryKey: ['partner-org-slots-all'],
    queryFn: getAllPartnerSlots,
  })

  const slotsByPartner = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of allSlots) {
      if (!s.partner_id) continue
      m.set(s.partner_id, (m.get(s.partner_id) ?? 0) + 1)
    }
    return m
  }, [allSlots])

  const stats = useMemo(() => {
    const total = partners.length
    const active = partners.filter((p) => p.status === 'active').length
    const trial = partners.filter((p) => p.status === 'trial').length
    const totalInstances = allSlots.filter((s) => s.partner_id).length
    return { total, active, trial, totalInstances }
  }, [partners, allSlots])

  const loading = loadingPartners || loadingSlots

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">{t('partners.title')}</h1>
        <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
          {t('partners.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title={t('partners.totalPartners')} value={stats.total} icon={Handshake} color="#6366f1" />
        <StatCard title={t('partners.active')} value={stats.active} icon={Building2} color="#22c55e" />
        <StatCard title={t('partners.trial')} value={stats.trial} icon={Clock} color="#f59e0b" />
        <StatCard title={t('partners.totalInstances')} value={stats.totalInstances} icon={Server} color="#3b82f6" />
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-white">{t('partners.allPartners')}</h2>
        <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
          <table className="min-w-full text-sm" style={{ background: '#1a1d27' }}>
            <thead>
              <tr style={{ background: '#252836', color: '#9ca3af' }}>
                <th className="text-left font-medium px-4 py-3">{t('partners.partnerName')}</th>
                <th className="text-left font-medium px-4 py-3">{t('common.status')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.contactEmail')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.instances')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.dailyRate')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.slug')}</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center" style={{ color: '#6b7280' }}>
                    {t('partners.noPartners')}
                  </td>
                </tr>
              )}
              {partners.map((p) => {
                const count = slotsByPartner.get(p.id) ?? 0
                return (
                  <tr
                    key={p.id}
                    className="cursor-pointer transition-colors hover:bg-white/5"
                    style={{ borderTop: '1px solid #2d3148' }}
                    onClick={() => navigate(`/partners/${p.id}`)}
                  >
                    <td className="px-4 py-3 text-white font-medium">{p.display_name}</td>
                    <td className="px-4 py-3">
                      <PartnerStatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#9ca3af' }}>
                      {p.contact_email ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      <span className="font-mono num">{count}</span>
                      <span style={{ color: '#6b7280' }}> / </span>
                      <span className="font-mono num">{p.max_org_slots}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-200 font-mono num">{formatShekels(p.daily_rate_agorot)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-indigo-300">{p.slug}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
